import { css } from 'aphrodite';
import * as React from 'react';
import { ErrorMessageViewModel, IAccount, IWillRenewPredictionReason } from '../../../extViewmodels';
import { IAccountSnapshot } from '../../../models/AdminModels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { invalidateAccountAdminQuery } from '../../../queries/Admin/Account/useAdminAccountQuery';
import { AccountSnapshotViewModel } from '../../../viewmodels/AdminViewModels';
import { Button as LegacyButton } from '../../../web/components/Button';
import { Modal } from '../../../web/components/Modal';
import { SelectBox } from '../../../web/components/SelectBox';
import { Button } from '../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../web/components-v2/Dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { TextArea } from '../../../web/components-v2/TextArea';
import { invalidateMonthlyRenewalQueries } from '../../containers/reports/MonthlyRenewalAccounts';
import { PredictionReasonIndicator } from './PredictionReasonIndicator';
import {
	CHURN_REASON_CATEGORIES,
	getWillRenewPredictionReasonCategoryTitle,
	POSITIVE_REASON_CATEGORIES,
	WillRenewPredictionReasonCategory,
} from './reasonCategories';
import { styleSheet } from './styles';

enum WillRenewPredictionEnum {
	NotSelected = 'Not Selected',
	Yes = 'Yes',
	No = 'No',
	Downgrade = 'Downgrade (MGR Only)',
}

/** API values for the prediction (sent verbatim; empty string represents "not set"). */
const PREDICTION_YES = 'Yes';
const PREDICTION_NO = 'No';

// Type to represent the selected prediction
type WillRenewPrediction = {
	type: string;
	percentage?: number;
};

type ReasonModalMode = 'flip' | 'edit';

type ReasonModalState = {
	isOpen: boolean;
	mode: ReasonModalMode;
	/** The prediction value the reason is being captured for ('Yes' | 'No' | ''). */
	predictionValue: string;
	category: string;
	detail: string;
};

const CLOSED_REASON_MODAL: ReasonModalState = {
	isOpen: false,
	mode: 'flip',
	predictionValue: '',
	category: '',
	detail: '',
};

type Props = {
	snapshot?: AccountSnapshotViewModel;
	account?: IAccount;
	errorMessagesVM: ErrorMessageViewModel;
	/**
	 * Called after a prediction change is successfully persisted, so a parent (e.g. the account
	 * detail form) can sync its local state. Receives the persisted value and the saved reason
	 * (when one was captured).
	 */
	onChange?: (selection: string, reason?: IWillRenewPredictionReason) => void;
	/** When true, renders an icon + popover to view/edit the saved reason (used in the tight snapshot grid). */
	renderReasonIndicator?: boolean;
};

/**
 * A reason (category + free-text detail) is required when the change reflects a meaningful shift in
 * renewal sentiment: predicting churn ("No" from any other state) or recovering ("No" -> "Yes").
 * The optimistic "not set -> Yes" path stays frictionless.
 */
const requiresReason = (previousValue: string, nextValue: string): boolean => {
	if (nextValue === PREDICTION_NO) {
		return previousValue !== PREDICTION_NO;
	}
	if (nextValue === PREDICTION_YES) {
		return previousValue === PREDICTION_NO;
	}
	return false;
};

export const WillRenewPredictionSelect = ({
	snapshot,
	account,
	errorMessagesVM,
	onChange,
	renderReasonIndicator,
}: Props) => {
	const userSession = useUserSession();
	const { logApiError } = useEventLogging();

	// Parse the initial value to get type and percentage
	const parseInitialValue = (): WillRenewPrediction => {
		const initialValue =
			snapshot?.updatedWillRenewPrediction ??
			snapshot?.accountModel?.additionalInfo?.willRenewPrediction ??
			(account?.additionalInfo?.willRenewPrediction || WillRenewPredictionEnum.NotSelected);

		// If it's a numeric string, it's a downgrade percentage
		if (/^\d+$/.test(initialValue)) {
			return {
				type: WillRenewPredictionEnum.Downgrade,
				percentage: parseInt(initialValue, 10),
			};
		}

		return { type: initialValue };
	};

	const [prediction, setPrediction] = React.useState<WillRenewPrediction>(parseInitialValue());
	const [reason, setReason] = React.useState<IWillRenewPredictionReason | undefined>(
		snapshot?.willRenewPredictionReason ?? account?.additionalInfo?.willRenewPredictionReason
	);
	const [reasonModal, setReasonModal] = React.useState<ReasonModalState>(CLOSED_REASON_MODAL);
	const [reasonError, setReasonError] = React.useState<string | null>(null);
	const [isSavingReason, setIsSavingReason] = React.useState(false);
	const [downgradePercentage, setDowngradePercentage] = React.useState<string>('');
	const [showDowngradeModal, setShowDowngradeModal] = React.useState<boolean>(false);

	// Guard against setState after unmount / in-flight requests resolving on a removed row.
	const isMountedRef = React.useRef(true);
	React.useEffect(() => {
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	// Keep the locally-displayed reason in sync when a parent refetch (e.g. after
	// invalidateMonthlyRenewalQueries, or another specialist's edit) yields a fresh reason.
	const propReason = snapshot?.willRenewPredictionReason ?? account?.additionalInfo?.willRenewPredictionReason;
	React.useEffect(() => {
		setReason(propReason);
	}, [propReason]);

	/** Map the API prediction value back to the dropdown enum so the trigger reflects the saved state. */
	const predictionTypeFromValue = (value: string): string => {
		if (value === PREDICTION_YES) {
			return WillRenewPredictionEnum.Yes;
		}
		if (value === PREDICTION_NO) {
			return WillRenewPredictionEnum.No;
		}
		return WillRenewPredictionEnum.NotSelected;
	};

	/** Map the current dropdown enum to the API prediction value. */
	const valueFromPredictionType = (type: string): string => {
		if (type === WillRenewPredictionEnum.Yes) {
			return PREDICTION_YES;
		}
		if (type === WillRenewPredictionEnum.No) {
			return PREDICTION_NO;
		}
		return '';
	};

	/**
	 * Persists the prediction (and optional reason) via the dedicated endpoint. Mirrors the pending
	 * cancellation flow: the change is applied immediately and the API creates a CSM note whenever a
	 * reason is supplied. Returns true on success.
	 */
	const persistPrediction = async (value: string, category?: string, detail?: string): Promise<boolean> => {
		const body: { value: string; category?: string; detail?: string } = { value };
		if (category) {
			body.category = category;
		}
		if (detail) {
			body.detail = detail;
		}

		if (snapshot) {
			const response = await userSession.webServiceHelper.callWebServiceAsync<IAccountSnapshot>(
				`admin/accountSnapshot/${snapshot.id}/willRenewPrediction`,
				'PATCH',
				body
			);
			if (!isMountedRef.current) {
				return false;
			}
			if (!response.success) {
				errorMessagesVM.pushApiError(response);
				logApiError('SnapshotUpdateRenewalPrediction-Error', response);
				return false;
			}
			invalidateMonthlyRenewalQueries();
			const updatedReason = response.value?.willRenewPredictionReason;
			if (updatedReason) {
				setReason(updatedReason);
			}
			onChange?.(value, updatedReason);
			return true;
		}

		if (account) {
			const response = await userSession.webServiceHelper.callWebServiceAsync<IAccount>(
				`admin/account/${account.id}/additionalInfo/willRenewPrediction`,
				'PATCH',
				body
			);
			if (!isMountedRef.current) {
				return false;
			}
			if (!response.success) {
				errorMessagesVM.pushApiError(response);
				logApiError('AccountUpdateRenewalPrediction-Error', response);
				return false;
			}
			invalidateMonthlyRenewalQueries();
			// Refresh the admin account cache so a return visit to the account page reflects the change.
			invalidateAccountAdminQuery();
			const updatedReason = response.value?.additionalInfo?.willRenewPredictionReason;
			if (updatedReason) {
				setReason(updatedReason);
			}
			onChange?.(value, updatedReason);
			return true;
		}

		return false;
	};

	const onSelectedWillRenewPredictionChange = async (selection: string) => {
		if (selection === WillRenewPredictionEnum.Downgrade) {
			setShowDowngradeModal(true);
			return;
		}

		const previousValue = valueFromPredictionType(prediction.type);
		const nextValue = valueFromPredictionType(selection);

		// Reason-required flips defer persistence until the reason modal is submitted. The dropdown
		// keeps showing the previous value (it is controlled by `prediction`) until then.
		if (requiresReason(previousValue, nextValue)) {
			setReasonError(null);
			setReasonModal({
				isOpen: true,
				mode: 'flip',
				predictionValue: nextValue,
				category: '',
				detail: '',
			});
			return;
		}

		const success = await persistPrediction(nextValue);
		if (success) {
			setPrediction({ type: selection });
		}
	};

	const openReasonModalForEdit = () => {
		setReasonError(null);
		setReasonModal({
			isOpen: true,
			mode: 'edit',
			// Prefer the prediction the reason was captured against so the category group matches the
			// saved category; fall back to the current prediction.
			predictionValue: reason?.prediction || valueFromPredictionType(prediction.type),
			category: reason?.category ?? '',
			detail: reason?.detail ?? '',
		});
	};

	const closeReasonModal = () => {
		setReasonModal(CLOSED_REASON_MODAL);
		setReasonError(null);
		setIsSavingReason(false);
	};

	const handleReasonSubmit = async () => {
		const category = reasonModal.category;
		const detail = reasonModal.detail.trim();
		if (!category) {
			setReasonError('Please select a reason category.');
			return;
		}
		if (!detail) {
			setReasonError('Please enter a prediction detail.');
			return;
		}

		setReasonError(null);
		setIsSavingReason(true);
		const success = await persistPrediction(reasonModal.predictionValue, category, detail);
		if (!isMountedRef.current) {
			return;
		}
		setIsSavingReason(false);
		if (success) {
			setPrediction({ type: predictionTypeFromValue(reasonModal.predictionValue) });
			closeReasonModal();
		}
	};

	const handleDowngradeSubmit = async () => {
		const numericValue = parseInt(downgradePercentage, 10);
		if (isNaN(numericValue) || numericValue < 10 || numericValue > 100) {
			// Show validation error
			return;
		}

		// Store just the numeric value
		const apiValue = numericValue.toString();
		const success = await persistPrediction(apiValue);
		if (!isMountedRef.current) {
			return;
		}
		if (success) {
			setPrediction({ type: WillRenewPredictionEnum.Downgrade, percentage: numericValue });
			closeDowngradeModal();
		}
	};

	const closeDowngradeModal = () => {
		setShowDowngradeModal(false);
		setDowngradePercentage('');
	};

	const options = React.useMemo(
		() =>
			Object.values(WillRenewPredictionEnum).map(predictionType => {
				return { value: predictionType, title: predictionType };
			}),
		[]
	);

	const reasonCategoryOptions = React.useMemo(() => {
		const group = reasonModal.predictionValue === PREDICTION_NO ? CHURN_REASON_CATEGORIES : POSITIVE_REASON_CATEGORIES;
		// Guarantee the selected category is always an option, even when editing a saved reason whose
		// category belongs to the other direction's group (otherwise the Select can't display it).
		const selected = reasonModal.category as WillRenewPredictionReasonCategory;
		if (selected && !group.includes(selected)) {
			return [...group, selected];
		}
		return group;
	}, [reasonModal.predictionValue, reasonModal.category]);

	const reasonModalTitle =
		reasonModal.mode === 'edit'
			? 'Edit Renewal Prediction Reason'
			: reasonModal.predictionValue === PREDICTION_NO
				? 'Why is this account predicted not to renew?'
				: 'What changed for this account?';

	return (
		<>
			<div className={css(styleSheet.container)}>
				<SelectBox
					triggerClassName={css(styleSheet.selectTrigger)}
					selectedOption={options.find(option => option.value === prediction.type)}
					onSelectionChanged={selectedOption => onSelectedWillRenewPredictionChange(selectedOption.value)}
					options={options}
					triggerStyles={[styleSheet.selectOption]}
				/>
				{prediction.type === WillRenewPredictionEnum.Downgrade && prediction.percentage && (
					<span className={css(styleSheet.percentageLabel)}>({prediction.percentage}%)</span>
				)}
				{renderReasonIndicator && (
					// Always reserve the icon slot so the dropdowns align across rows whether or not a
					// reason exists; the icon only renders when there is a saved reason.
					<div className={css(styleSheet.reasonIndicatorSlot)}>
						<PredictionReasonIndicator reason={reason} onEdit={openReasonModalForEdit} />
					</div>
				)}
			</div>

			<Dialog
				open={reasonModal.isOpen}
				onOpenChange={open => {
					if (!open) {
						closeReasonModal();
					}
				}}
				dismissOnOutsideClick={false}
			>
				<DialogPopup styles={[styleSheet.modal]}>
					<DialogHeader>
						<DialogTitle>{reasonModalTitle}</DialogTitle>
					</DialogHeader>
					<DialogBody>
						<div className={css(styleSheet.reasonField)}>
							<label className={css(styleSheet.reasonFieldLabel)} htmlFor='will-renew-prediction-category'>
								Reason category<span className={css(styleSheet.requiredMark)}>*</span>
							</label>
							<Select
								value={reasonModal.category}
								onValueChange={value => setReasonModal(prev => ({ ...prev, category: value }))}
							>
								<SelectTrigger id='will-renew-prediction-category' styles={styleSheet.reasonSelectTrigger}>
									<SelectValue placeholder='Select reason category' />
								</SelectTrigger>
								<SelectContent>
									{reasonCategoryOptions.map(category => (
										<SelectItem key={category} value={category}>
											{getWillRenewPredictionReasonCategoryTitle(category)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className={css(styleSheet.reasonField)}>
							<label className={css(styleSheet.reasonFieldLabel)} htmlFor='will-renew-prediction-detail'>
								Prediction detail<span className={css(styleSheet.requiredMark)}>*</span>
							</label>
							<TextArea
								id='will-renew-prediction-detail'
								placeholder='Prediction detail'
								rows={4}
								value={reasonModal.detail}
								onChange={e => setReasonModal(prev => ({ ...prev, detail: e.target.value }))}
							/>
						</div>
						{!!reasonError && <div className={css(styleSheet.errorText)}>{reasonError}</div>}
					</DialogBody>
					<DialogFooter>
						<div className={css(styleSheet.dialogFooter)}>
							<Button variant='secondary' onClick={closeReasonModal} disabled={isSavingReason}>
								Cancel
							</Button>
							<Button variant='primary' onClick={handleReasonSubmit} isLoading={isSavingReason}>
								Save
							</Button>
						</div>
					</DialogFooter>
				</DialogPopup>
			</Dialog>

			<Modal
				isOpen={showDowngradeModal}
				onRequestClose={closeDowngradeModal}
				useDefaultHeader={true}
				shouldCloseOnOverlayClick={false}
				name='downgrade-percentage-modal'
			>
				<div className={css(styleSheet.modalContent)}>
					<h3 className={css(styleSheet.modalTitle)}>Enter Renewal Percentage</h3>
					<p className={css(styleSheet.modalInstruction)}>Please enter a number between 10-100:</p>
					<input
						type='number'
						min='10'
						max='100'
						value={downgradePercentage}
						onChange={e => setDowngradePercentage(e.target.value)}
						className={css(styleSheet.input)}
					/>
					<div className={css(styleSheet.modalButtons)}>
						<LegacyButton
							kind='primary'
							label='Submit'
							onClick={handleDowngradeSubmit}
							className={css(styleSheet.submitButton)}
						/>
						<LegacyButton
							kind='reverse'
							label='Cancel'
							onClick={closeDowngradeModal}
							className={css(styleSheet.cancelButton)}
						/>
					</div>
				</div>
			</Modal>
		</>
	);
};
